import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Gauge } from "lucide-react";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { Card } from "@shared/ui/containers/Card";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import {
  useScopedUtilityLinks,
  type ScopedUtilityLink,
} from "./useScopedUtilityLinks";
import { useUsageActionMeta } from "./useUtilityLinkActions";
import { useUtilityLinkUsage } from "./useUtilityLinkUsage";
import { getUtilityPresentation, utilityIconBg } from "./utilityPresentation";
import { formatUsageTotal } from "./utilityUsageMath";

/**
 * Utilities list — scoped to the active property (same filter as home widgets).
 * Utilities are the devices the estate has set up at your property; there is no
 * self-linking step.
 */
export function MyUtilities(): JSX.Element {
  const navigate = useNavigate();
  const { scopedLinks, activeProperty, isLoading, isError, isEmpty, refetch } =
    useScopedUtilityLinks();

  const subtitle = activeProperty
    ? `Usage for ${activeProperty.unit_label}${activeProperty.estate_name ? ` · ${activeProperty.estate_name}` : ""}.`
    : "Smart meters and connected devices at your home. Tap one to view live usage.";

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow="Utilities"
        title="My utilities."
        subtitle={subtitle}
        backTo="/app/home"
      />

      <div className="flex flex-col gap-3 px-5 pt-5 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        {isLoading ? (
          <>
            <Skeleton className="h-20 w-full rounded-2xl" />
            <Skeleton className="h-20 w-full rounded-2xl" />
          </>
        ) : isError ? (
          <ErrorState onRetry={() => refetch()} />
        ) : isEmpty ? (
          <EmptyState
            icon={<Gauge className="h-7 w-7" strokeWidth={1.5} />}
            title="No utilities for this property"
            description="No meters are set up for your selected property yet. The estate team configures these — check back once setup is complete."
          />
        ) : (
          scopedLinks.map((link) => (
            <UtilityLinkRow
              key={link.link_id}
              link={link}
              onOpen={() => navigate(`/app/utilities/${link.link_id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function UtilityLinkRow({
  link,
  onOpen,
}: {
  link: ScopedUtilityLink;
  onOpen: () => void;
}): JSX.Element {
  const presentation = getUtilityPresentation(link.utilityName);
  const Icon = presentation.icon;
  const { meta, isMetaReady } = useUsageActionMeta(link.link_id);
  const timeframe = meta?.homeWidget.timeframe ?? "";
  const usageQuery = useUtilityLinkUsage(link.link_id, timeframe, {
    enabled: isMetaReady && !!timeframe,
  });
  const identifier = useMemo(
    () => identifierSummary(link.identifier_payload),
    [link.identifier_payload],
  );

  const weekTotal = useMemo(() => {
    if (!usageQuery.data) return null;
    return formatUsageTotal(
      usageQuery.data.total_consumption,
      usageQuery.data.unit,
    );
  }, [usageQuery.data]);

  return (
    <Card asChild noGrain className="rounded-2xl p-4 text-left shadow-sm">
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-3"
      >
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill"
          style={{ backgroundColor: utilityIconBg(presentation.color) }}
        >
          <Icon
            className="h-5 w-5"
            strokeWidth={1.75}
            style={{ color: presentation.color }}
          />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-headline text-ink truncate">
            {link.utilityName}
          </p>
          {identifier ? (
            <p className="mt-0.5 text-caption text-ink-muted truncate">
              {identifier}
            </p>
          ) : (
            <p className="mt-0.5 text-caption text-ink-muted">
              {meta?.homeWidget.summaryLabel ?? "This week"}
            </p>
          )}
        </div>
        {!isMetaReady || usageQuery.isLoading ? (
          <Skeleton className="h-5 w-14 shrink-0" />
        ) : weekTotal ? (
          <span className="shrink-0 text-body-sm font-semibold text-ink">
            {weekTotal.amount}
            {weekTotal.unit ? (
              <span className="text-caption font-medium text-ink-muted">
                {" "}
                {weekTotal.unit}
              </span>
            ) : null}
          </span>
        ) : null}
        <span className="rounded-pill bg-sunken px-2.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-ink-secondary">
          {link.status}
        </span>
      </button>
    </Card>
  );
}

function identifierSummary(
  payload: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const parts: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined || value === "") continue;
    parts.push(`${humanise(key)}: ${value}`);
    if (parts.length >= 2) break;
  }
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function humanise(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
